export type Language = 'en' | 'sv';

export const translations = {
  en: {
    hero: {
      badge: 'Your health data, your control',
      title: 'Understand Your Health Data',
      subtitle: 'Your health is your life. Understanding your own health through improved health literacy is the single best way to improve your life. Download your records from 1177.se, understand them with AI-powered explanations, and get personalized insights—all while keeping your data private and secure.',
      trustBadges: {
        gdpr: 'GDPR Compliant',
        privacy: 'Privacy-First',
        zeroStorage: 'Zero Data Storage',
      },
      cta: {
        startFree: 'Start Free',
        learnMore: 'Learn More',
      },
      features: [
        'Download health records from 1177.se with one click',
        'AI explains medical terminology in plain language',
        'Complete health assessments tailored for Swedish care pathways',
        'Get personalized health actions based on your records',
        'Track your health journey with daily reflections',
        'Join a community focused on patient empowerment',
      ],
    },
    howItWorks: {
      eyebrow: 'How It Works',
      title: 'Three simple steps to health data empowerment',
      subtitle: 'Take control of your health data in minutes. Download, understand, and act on your medical records with complete privacy.',
      steps: [
        {
          title: 'Download Your Data',
          description: 'Use BankID authentication directly within the app to securely download your complete health records from 1177.se or other Swedish healthcare systems. Your data is stored on your device and sent to our LLM provider with a zero data retention policy—they never store your information.',
        },
        {
          title: 'AI Explains Everything',
          description: 'Our AI assistant analyzes your records and explains medical terminology in plain language. Understand diagnoses, lab results, and treatment plans without needing a medical degree.',
        },
        {
          title: 'Get Personalized Insights',
          description: 'Receive personalized health assessments, action recommendations, and track your progress over time. All powered by AI, all private and secure.',
        },
      ],
    },
    security: {
      eyebrow: 'Security & Privacy',
      title: 'Your data stays yours—always',
      subtitle: 'We built EIR Space with privacy as the foundation. Your health data is stored on your device and sent to our LLM provider with a zero data retention policy—they never store your information, and we never store it on our servers.',
      cards: [
        {
          title: 'Zero Data Storage',
          description: 'We never store your health data on our servers. Your data is stored on your device and sent to our LLM provider for processing with a zero data retention policy—they never store your information. Your records remain completely private and under your control.',
        },
        {
          title: 'GDPR Compliant',
          description: 'Fully compliant with EU data protection regulations. All data handling follows GDPR principles, and we\'re transparent about how your information is processed.',
        },
        {
          title: 'BankID Integration',
          description: 'Secure authentication using Swedish BankID. Your identity is verified through Sweden\'s national ID system, ensuring maximum security and trust.',
        },
      ],
    },
    socialProof: {
      eyebrow: 'Trusted by Patients',
      title: 'Join thousands taking control of their health',
      subtitle: 'Patients across Sweden are using EIR Space to understand and manage their health data.',
      stats: {
        recordsAnalyzed: 'Health Records Analyzed',
        activeUsers: 'Active Users',
        uptime: 'Uptime',
        dataBreaches: 'Data Breaches',
      },
      testimonials: [
        {
          quote: 'EIR Space helped me understand my medical records for the first time. The AI explanations made everything clear, and I finally feel in control of my health data.',
          name: 'Maria Andersson',
          role: 'Patient, Stockholm',
        },
        {
          quote: 'As someone with multiple chronic conditions, having all my records in one place with AI-powered insights has been life-changing. I can now have informed conversations with my doctors.',
          name: 'Erik Johansson',
          role: 'Patient, Gothenburg',
        },
        {
          quote: 'The privacy-first approach gives me confidence. I know my data stays on my device, and the AI helps me understand complex medical information without compromising my privacy.',
          name: 'Anna Lindqvist',
          role: 'Patient, Malmö',
        },
      ],
    },
    highlights: [
      {
        title: 'Download from 1177.se',
        description: 'Download your complete health records directly from Swedish healthcare systems using BankID authentication within the app. Your data is stored on your device and processed by our LLM provider with zero data retention for maximum privacy.',
      },
      {
        title: 'AI-Powered Understanding',
        description: 'Get explanations of medical terminology, diagnoses, and lab results in plain language. Understand your health data without needing medical training.',
      },
      {
        title: 'Swedish Healthcare Focus',
        description: 'Health assessments and programs tailored for Swedish care pathways. Built specifically for the Nordic healthcare system.',
      },
      {
        title: 'Complete Privacy',
        description: 'Your data is stored on your device and processed by our LLM provider with zero data retention. GDPR compliant, and BankID integration for secure authentication.',
      },
    ],
    features: {
      assessment: {
        eyebrow: 'Health Assessment',
        title: 'Understand your wellbeing with structured check-ins',
        subtitle: 'Complete evidence-based health assessments tailored for Swedish care pathways. Track your progress over time and unlock personalized AI insights based on your health records.',
        cta: 'Start Assessment',
      },
      actions: {
        eyebrow: 'Health Actions',
        title: 'Turn insights into concrete next steps',
        subtitle: 'Get personalized health actions that help you improve your health. Each action takes a maximum of 15 minutes to complete.',
        cta: 'Explore Actions',
      },
      feedback: {
        eyebrow: 'Feedback Studio',
        title: 'Gather loving feedback from your support network',
        subtitle: 'Invite friends, family, or care partners to share their perspective on your health journey in a supportive way.',
        cta: 'Launch Feedback Form',
      },
      community: {
        eyebrow: 'From the Community',
        title: 'Health programs crafted and shared with heart',
        subtitle: 'Explore compassionate, structured journeys authored by peers and practitioners. Every program is reviewed by the community before it reaches you.',
        cta: 'Explore All Programs',
      },
    },
    video: {
      eyebrow: 'Getting Started',
      title: 'See how EIR Space works',
      subtitle: 'Watch a quick introduction to understand how to download your health data, explore your records, and get personalized insights.',
    },
    invitation: {
      title: 'Ready to know your health—and yourself—more deeply?',
      subtitle: 'Step into a calmer, kinder space for your health journey. Ask questions, build rituals, and feel progress happen in real time.',
      cta: {
        startFree: 'Start Free',
        learnMore: 'Learn More',
      },
    },
    footer: {
      crafted: 'Crafted with care by the Eir team',
      caption: 'EIR Space empowers you to understand and control your health data. GDPR compliant, privacy-first, and built for Swedish healthcare.',
      links: {
        docs: 'Eir Docs',
        healthLiteracy: 'Health Literacy',
        feedback: 'Feedback',
        requestCare: 'Request Care',
        privacy: 'Privacy Policy',
        se1177: '1177.se',
      },
    },
    chat: {
      header: {
        title: 'EIR Health Assistant',
        subtitle: 'Ask me anything about your health',
      },
      welcome: {
        title: '👋 Welcome to EIR!',
        message: 'I\'m your AI health assistant. Ask me anything about your health, and I\'ll provide personalized insights based on your clinical records.',
      },
      placeholder: {
        default: 'Ask about your health...',
        followUp: 'Ask another question...',
      },
      transition: '✨ Send another message to continue in the full app',
    },
  },
  sv: {
    hero: {
      badge: 'Din hälsodata, din kontroll',
      title: 'Förstå din hälsodata',
      subtitle: 'Din hälsa är ditt liv. Att förstå din egen hälsa genom förbättrad hälsokunskap är det bästa sättet att förbättra ditt liv. Ladda ner dina journaler från 1177.se, förstå dem med AI-drivna förklaringar och få personliga insikter—allt medan din data förblir privat och säker.',
      trustBadges: {
        gdpr: 'GDPR-kompatibel',
        privacy: 'Integritet först',
        zeroStorage: 'Ingen datalagring',
      },
      cta: {
        startFree: 'Kom igång',
        learnMore: 'Läs mer',
      },
      features: [
        'Ladda ner hälsodata från 1177.se med ett klick',
        'AI förklarar medicinsk terminologi i enkelt språk',
        'Genomför hälsoundersökningar anpassade för svenska vårdvägar',
        'Få personliga hälsåtgärder baserat på dina journaler',
        'Följ din hälsoresa med dagliga reflektioner',
        'Gå med i en gemenskap fokuserad på patientmakt',
      ],
    },
    howItWorks: {
      eyebrow: 'Så fungerar det',
      title: 'Tre enkla steg till hälsodatamakt',
      subtitle: 'Ta kontroll över din hälsodata på minuter. Ladda ner, förstå och agera på dina medicinska journaler med fullständig integritet.',
      steps: [
        {
          title: 'Ladda ner din data',
          description: 'Använd BankID-autentisering direkt i appen för att säkert ladda ner dina kompletta hälsodata från 1177.se eller andra svenska vårdsystem. Din data lagras på din enhet och skickas till vår LLM-leverantör med noll datalagringspolicy—de lagrar aldrig din information.',
        },
        {
          title: 'AI förklarar allt',
          description: 'Vår AI-assistent analyserar dina journaler och förklarar medicinsk terminologi i enkelt språk. Förstå diagnoser, laboratorieresultat och behandlingsplaner utan att behöva medicinsk utbildning.',
        },
        {
          title: 'Få personliga insikter',
          description: 'Få personliga hälsoundersökningar, åtgärdsrekommendationer och följ din utveckling över tid. Allt drivet av AI, allt privat och säkert.',
        },
      ],
    },
    security: {
      eyebrow: 'Säkerhet & Integritet',
      title: 'Din data förblir din—alltid',
      subtitle: 'Vi byggde EIR Space med integritet som grund. Din hälsodata lagras på din enhet och skickas till vår LLM-leverantör med noll datalagringspolicy—de lagrar aldrig din information, och vi lagrar den aldrig på våra servrar.',
      cards: [
        {
          title: 'Ingen datalagring',
          description: 'Vi lagrar aldrig din hälsodata på våra servrar. Din data lagras på din enhet och skickas till vår LLM-leverantör för bearbetning med noll datalagringspolicy—de lagrar aldrig din information. Dina journaler förblir helt privata och under din kontroll.',
        },
        {
          title: 'GDPR-kompatibel',
          description: 'Helt kompatibel med EU:s dataskyddsförordningar. All datahantering följer GDPR-principer, och vi är transparenta om hur din information bearbetas.',
        },
        {
          title: 'BankID-integration',
          description: 'Säker autentisering med svenska BankID. Din identitet verifieras genom Sveriges nationella ID-system, vilket säkerställer maximal säkerhet och tillit.',
        },
      ],
    },
    socialProof: {
      eyebrow: 'Betrodd av patienter',
      title: 'Gå med i tusentals som tar kontroll över sin hälsa',
      subtitle: 'Patienter över hela Sverige använder EIR Space för att förstå och hantera sin hälsodata.',
      stats: {
        recordsAnalyzed: 'Hälsodata analyserade',
        activeUsers: 'Aktiva användare',
        uptime: 'Uptime',
        dataBreaches: 'Dataläckor',
      },
      testimonials: [
        {
          quote: 'EIR Space hjälpte mig förstå mina medicinska journaler för första gången. AI-förklaringarna gjorde allt tydligt, och jag känner mig äntligen i kontroll över min hälsodata.',
          name: 'Maria Andersson',
          role: 'Patient, Stockholm',
        },
        {
          quote: 'Som någon med flera kroniska tillstånd har det varit livsförändrande att ha alla mina journaler på ett ställe med AI-drivna insikter. Jag kan nu ha informerade samtal med mina läkare.',
          name: 'Erik Johansson',
          role: 'Patient, Göteborg',
        },
        {
          quote: 'Den integritetsförsta metoden ger mig förtroende. Jag vet att min data stannar på min enhet, och AI hjälper mig förstå komplex medicinsk information utan att kompromissa min integritet.',
          name: 'Anna Lindqvist',
          role: 'Patient, Malmö',
        },
      ],
    },
    highlights: [
      {
        title: 'Ladda ner från 1177.se',
        description: 'Ladda ner dina kompletta hälsodata direkt från svenska vårdsystem med BankID-autentisering i appen. Din data lagras på din enhet och bearbetas av vår LLM-leverantör med noll datalagring för maximal integritet.',
      },
      {
        title: 'AI-driven förståelse',
        description: 'Få förklaringar av medicinsk terminologi, diagnoser och laboratorieresultat i enkelt språk. Förstå din hälsodata utan att behöva medicinsk utbildning.',
      },
      {
        title: 'Svensk vårdfokus',
        description: 'Hälsoundersökningar och program anpassade för svenska vårdvägar. Byggt specifikt för det nordiska vårdsystemet.',
      },
      {
        title: 'Fullständig integritet',
        description: 'Din data lagras på din enhet och bearbetas av vår LLM-leverantör med noll datalagring. GDPR-kompatibel och BankID-integration för säker autentisering.',
      },
    ],
    features: {
      assessment: {
        eyebrow: 'Hälsoundersökning',
        title: 'Förstå ditt välmående med strukturerade kontroller',
        subtitle: 'Genomför evidensbaserade hälsoundersökningar anpassade för svenska vårdvägar. Följ din utveckling över tid och lås upp personliga AI-insikter baserat på din hälsodata.',
        cta: 'Starta undersökning',
      },
      actions: {
        eyebrow: 'Hälsåtgärder',
        title: 'Förvandla insikter till konkreta nästa steg',
        subtitle: 'Få personliga hälsåtgärder som hjälper dig förbättra din hälsa. Varje åtgärd tar maximalt 15 minuter att genomföra.',
        cta: 'Utforska åtgärder',
      },
      feedback: {
        eyebrow: 'Feedback Studio',
        title: 'Samla kärleksfull feedback från ditt stödnätverk',
        subtitle: 'Bjud in vänner, familj eller vårdpartners att dela sina perspektiv på din hälsoresa på ett stödjande sätt.',
        cta: 'Starta feedbackformulär',
      },
      community: {
        eyebrow: 'Från gemenskapen',
        title: 'Hälsoprogram skapade och delade med hjärta',
        subtitle: 'Utforska medkännande, strukturerade resor skapade av jämlikar och praktiker. Varje program granskas av gemenskapen innan det når dig.',
        cta: 'Utforska alla program',
      },
    },
    video: {
      eyebrow: 'Kom igång',
      title: 'Se hur EIR Space fungerar',
      subtitle: 'Titta på en snabb introduktion för att förstå hur du laddar ner din hälsodata, utforskar dina journaler och får personliga insikter.',
    },
    invitation: {
      title: 'Redo att lära känna din hälsa—och dig själv—djupare?',
      subtitle: 'Steg in i ett lugnare, vänligare utrymme för din hälsoresa. Ställ frågor, bygg ritualer och känn framsteg hända i realtid.',
      cta: {
        startFree: 'Kom igång',
        learnMore: 'Läs mer',
      },
    },
    footer: {
      crafted: 'Skapat med omsorg av Eir-teamet',
      caption: 'EIR Space ger dig möjlighet att förstå och kontrollera din hälsodata. GDPR-kompatibel, integritet först och byggd för svensk sjukvård.',
      links: {
        docs: 'Eir Docs',
        healthLiteracy: 'Hälsokunskap',
        feedback: 'Feedback',
        requestCare: 'Begär vård',
        privacy: 'Integritetspolicy',
        se1177: '1177.se',
      },
    },
    chat: {
      header: {
        title: 'EIR Hälsassistent',
        subtitle: 'Fråga mig vad som helst om din hälsa',
      },
      welcome: {
        title: '👋 Välkommen till EIR!',
        message: 'Jag är din AI-hälsassistent. Fråga mig vad som helst om din hälsa, så ger jag personliga insikter baserat på dina kliniska journaler.',
      },
      placeholder: {
        default: 'Fråga om din hälsa...',
        followUp: 'Ställ en till fråga...',
      },
      transition: '✨ Skicka ett till meddelande för att fortsätta i den fullständiga appen',
    },
  },
} as const;

